import { DealerState } from '@appTypes/DealerState';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import { DealType } from '../enums/DealType';
import { PortfolioFilterType } from '../enums/PortfolioFilterType';
import { SelectionToggleType } from '../enums/SelectionToggleType';
import { dateDifference, formatDate } from '../services/utils/DateUtils';
import { AppState } from '../store/AppState';
import {
  setDefaultBpsValue,
  setDefaultDealScoreValue,
  setDefaultInMarketScoreValue,
  setDefaultLastUpdated,
  setDefaultVehicleScoreValue,
  setIsPortfolioFiltersChanged,
  setPortfolioFilters,
  updateMasterFilterKey,
  updateSelectedFilters,
} from '../store/portfolio/PortfolioActions';
import { PortfolioState } from '../store/portfolio/PortfolioReducer';
import {
  FilterItemBrandResource,
  FilterItemResource,
  FilterRecallItemType,
  FilterStatusItemType,
  MinMaxFilter,
} from '../types/FilterTypes';
import {
  defaultStatus,
  initialBpsValue,
  initialDealScoreValue,
  initialInMarketScoreValue,
  initialLastUpdated,
  initialVehicleScoreValue,
  PortfolioFilterResource,
} from '../types/PortfolioFilterResource';
import { PortfolioRequestResource } from '../types/PortfolioRequestResource';
import { PortfolioView } from '../types/PortfolioView';
import { SortType } from '../types/SortType';
import { useDealType } from './UseDealType';
import { useHeapTrack } from './UseHeapTrack';

export const usePortfolioFilter = (): PortfolioFilterBoxType => {
  const { allView, leaseView, financeView, cashView } = useSelector<AppState, PortfolioState>(
    (state) => state.portfolio,
  );
  const { dealerInfos, pitchStatus, featureToggle, storeServices } = useSelector<
    AppState,
    DealerState
  >((state) => state.dealer);
  const { getDealTypeNumber } = useDealType();
  const dispatch = useDispatch();
  const storeCode = localStorage.getItem('am.storeCode');

  const fMCBrands = useMemo(() => {
    return dealerInfos?.some(
      (x) =>
        x.brandName.toLocaleUpperCase() === 'FORD' || x.brandName.toLocaleUpperCase() === 'LINCOLN',
    );
  }, [dealerInfos]);

  const getPortfolioView = (dealType: string): PortfolioView => {
    switch (dealType?.toLowerCase()) {
      case DealType.Lease.toLowerCase():
        return leaseView;
      case DealType.Cash.toLowerCase():
        return cashView;
      case DealType.Finance.toLowerCase():
        return financeView;
      default:
        return allView;
    }
  };

  const setSelectedFilter = (
    key: PortfolioFilterType,
    data:
      | FilterStatusItemType
      | FilterItemResource
      | FilterItemResource[]
      | MinMaxFilter<number>
      | MinMaxFilter<Date>
      | boolean,
    dealType: string,
    triggerSearch: boolean = true,
  ): void => {
    const currentView = getPortfolioView(dealType);
    const selectedFilters = currentView.selectedFilters;
    const filters = currentView.filters;
    if (triggerSearch) {
      dispatch(setIsPortfolioFiltersChanged(true, dealType));
    }
    const newSelectedFilters = { ...selectedFilters };
    switch (key) {
      case PortfolioFilterType.Status:
        const statusData = <FilterStatusItemType>data;
        if (statusData.key) {
          dispatch(updateMasterFilterKey(key, dealType));
        }
        newSelectedFilters[key] = <FilterStatusItemType>data;
        dispatch(
          updateSelectedFilters(
            { ...selectedFilters, status: cleanStatusFilter(<FilterStatusItemType>data, dealType) },
            dealType,
          ),
        );
        break;

      case PortfolioFilterType.CurrentVehicleTypes:
        if ((<FilterItemResource>data).key?.toLowerCase() !== 'all') {
          dispatch(updateMasterFilterKey(key, dealType));
        }

        newSelectedFilters[key] = <FilterItemResource>data;
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;

      case PortfolioFilterType.ReplacementVehicleSelection:
        newSelectedFilters[key] = <FilterItemResource>data;
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;

      case PortfolioFilterType.Recall:
        if ((<FilterRecallItemType[]>data).length > 0) {
          dispatch(updateMasterFilterKey(key, dealType));
        }
        newSelectedFilters[key] = cleanMultiFilters(filters[key], <FilterRecallItemType[]>data);
        newSelectedFilters[key].forEach((x) => {
          x.date = formatDate(x.date);
        });
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;

      case PortfolioFilterType.Assignee:
      case PortfolioFilterType.DealType:
      case PortfolioFilterType.CurrentVehicleModels:
      case PortfolioFilterType.CurrentVehicleTrims:
      case PortfolioFilterType.CurrentVehicleYears:
      case PortfolioFilterType.SoldBy:
      case PortfolioFilterType.CampaignActivityViewCounts:
      case PortfolioFilterType.Incentives:
      case PortfolioFilterType.FordIncentiveNames:
        // TODO: Update the key back to 'Private Incentive' once data is fixed
        if (
          (<FilterItemResource[]>data).length > 0 &&
          (<FilterItemResource[]>data).find((e) => e.key === 'Private incentive')
        ) {
          useHeapTrack('Mastermind - Incentives Filter', {
            StoreCode: storeCode,
            PrivateIncentiveSelected: true,
          });
        }
      // TODO: adding break will cause issues with filters

      case PortfolioFilterType.CampaignExpiryDates:
        if (
          (<FilterItemResource[]>data).length > 0 &&
          triggerSearch &&
          (<FilterItemResource[]>data).find((e) => e.label.startsWith('Expiring'))
        ) {
          useHeapTrack('Mastermind - Private Incentives Filter', {
            StoreCode: storeCode,
            PrivateIncentivesSelected: true,
          });
        }
      // TODO: adding break will cause issues with filters

      case PortfolioFilterType.Colors:
      case PortfolioFilterType.ReplacementVehicleModels:
      case PortfolioFilterType.ReplacementVehicleTrims:
      case PortfolioFilterType.ReplacementVehicleYears:
      case PortfolioFilterType.ReplacementVehicleTypes:
      case PortfolioFilterType.OptOutPreference:
      case PortfolioFilterType.Insights:
        if ((<FilterItemResource[]>data).length > 0 && triggerSearch) {
          dispatch(updateMasterFilterKey(key, dealType));
        }
        newSelectedFilters[key] = cleanMultiFilters(filters[key], <FilterItemResource[]>data);
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;

      case PortfolioFilterType.CurrentVehicleMakes:
      case PortfolioFilterType.ReplacementVehicleMakes:
        if ((<FilterItemBrandResource[]>data).length > 0 && triggerSearch) {
          dispatch(updateMasterFilterKey(key, dealType));
        }
        newSelectedFilters[key] = cleanMultiBrandFilters(
          filters[key],
          <FilterItemBrandResource[]>data,
        );
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;

      case PortfolioFilterType.BpsValue:
      case PortfolioFilterType.InMarketScore:
      case PortfolioFilterType.VehicleScore:
      case PortfolioFilterType.DealScore:
      case PortfolioFilterType.LastUpdate:
      case PortfolioFilterType.PaymentsRemaining:
      case PortfolioFilterType.CurrentVehicleRate:
      case PortfolioFilterType.ReplacementVehicleRate:
      case PortfolioFilterType.CurrentVehicleMileage:
      case PortfolioFilterType.ReplacementVehicleMileage:
      case PortfolioFilterType.MonthsOfOwnership:
      case PortfolioFilterType.AgeDays:
      case PortfolioFilterType.LastServiceDate:
        dispatch(updateMasterFilterKey(key, dealType));
        newSelectedFilters[key] = <MinMaxFilter<number>>data;
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;

      case PortfolioFilterType.IsTrimConfirmationRequired:
        dispatch(updateMasterFilterKey(key, dealType));
        dispatch(
          updateSelectedFilters(
            {
              ...selectedFilters,
              isTrimConfirmationRequired: <boolean>data,
            },
            dealType,
          ),
        );
        break;
      case PortfolioFilterType.ShopperActivity:
        if ((<FilterItemResource>data).key?.toLowerCase() !== 'none') {
          dispatch(updateMasterFilterKey(key, dealType));
        }

        newSelectedFilters[key] = <FilterItemResource>data;
        dispatch(updateSelectedFilters(newSelectedFilters, dealType));
        break;
      default:
        break;
    }
  };

  const hasFilters = (dealType: string): boolean => {
    const currentView = getPortfolioView(dealType);
    const selectedFilters = currentView.selectedFilters;
    return (
      selectedFilters.assignees.length > 0 ||
      selectedFilters.dealTypes.length > 0 ||
      selectedFilters.bpsValue.min !== 0 ||
      selectedFilters.bpsValue.max !== 100 ||
      selectedFilters.inMarketScore.min !== 0 ||
      selectedFilters.inMarketScore.max !== 100 ||
      selectedFilters.vehicleScore.min !== 0 ||
      selectedFilters.vehicleScore.max !== 100 ||
      selectedFilters.dealScore.min !== 0 ||
      selectedFilters.dealScore.max !== 100 ||
      selectedFilters.lastUpdated.min !== 0 ||
      selectedFilters.lastUpdated.max !== initialLastUpdated.max ||
      selectedFilters.isTrimConfirmationRequired !== null ||
      selectedFilters.colors.length > 0 ||
      selectedFilters.optOutPreference.length > 0 ||
      selectedFilters.currentVehicleMakes.length > 0 ||
      selectedFilters.currentVehicleModels.length > 0 ||
      selectedFilters.currentVehicleTrims.length > 0 ||
      selectedFilters.currentVehicleYears.length > 0 ||
      selectedFilters.currentVehicleTypes?.key != null ||
      selectedFilters.currentVehicleMileage != null ||
      selectedFilters.monthsOfOwnership != null ||
      selectedFilters.paymentsRemaining != null ||
      selectedFilters.currentVehicleRate != null ||
      selectedFilters.incentives.length > 0 ||
      selectedFilters.campaignActivityViewCounts.length > 0 ||
      selectedFilters.soldBy.length > 0 ||
      selectedFilters.replacementVehicleMakes.length > 0 ||
      selectedFilters.replacementVehicleMakes.length > 0 ||
      selectedFilters.replacementVehicleModels.length > 0 ||
      selectedFilters.replacementVehicleTrims.length > 0 ||
      selectedFilters.replacementVehicleSelection?.key != null ||
      selectedFilters.replacementVehicleTypes.length > 0 ||
      selectedFilters.replacementVehicleYears.length > 0 ||
      selectedFilters.replacementVehicleMileage != null ||
      selectedFilters.replacementVehicleRate != null ||
      selectedFilters.insights.length > 0 ||
      selectedFilters.ageDays !== null ||
      selectedFilters.status?.key !== null ||
      selectedFilters.lastServiceDate !== null ||
      selectedFilters.recall.length > 0 ||
      selectedFilters.campaignExpiryDates.length > 0 ||
      selectedFilters.fordIncentiveNames.length > 0 ||
      selectedFilters.shopperActivity?.key != 'None'
    );
  };

  const updateWithMasterFilter = (data: PortfolioFilterResource, dealType: string): void => {
    const currentView = getPortfolioView(dealType);
    const masterFilterKey = currentView.masterFilterKey;
    const filters = currentView.filters;
    let masterKeyData: any;
    if (masterFilterKey) {
      switch (masterFilterKey) {
        case PortfolioFilterType.Status:
          const currentSelectedStatus = currentView.selectedFilters.status;
          const newData = data.status.find((x) => x.key === currentSelectedStatus.key);
          if (newData && currentSelectedStatus.subStatuses) {
            const currentSubStatus =
              currentSelectedStatus.subStatuses && currentSelectedStatus.subStatuses[0];
            const newSubStatus = newData.subStatuses?.find((y) => y.key === currentSubStatus.key);
            if (!newSubStatus) {
              const itemToPush = { ...currentSubStatus, count: 0 };
              newData.subStatuses = newData.subStatuses
                ? [...newData.subStatuses, itemToPush]
                : [itemToPush];
            }
          }
          // handled get count from ES on gateway.
          break;

        case PortfolioFilterType.CurrentVehicleTypes:
          masterKeyData = [...filters.currentVehicleTypes];
          updateSingleSelectDropdown(masterKeyData, data, PortfolioFilterType.CurrentVehicleTypes);
          break;

        case PortfolioFilterType.BpsValue:
          data.maxBps = filters.maxBps;
          data.minBps = filters.minBps;
          break;

        case PortfolioFilterType.InMarketScore:
          data.maxInMarketScore = filters.maxInMarketScore;
          data.minInMarketScore = filters.minInMarketScore;
          break;
        case PortfolioFilterType.VehicleScore:
          data.maxVehicleScore = filters.maxVehicleScore;
          data.minVehicleScore = filters.minVehicleScore;
          break;
        case PortfolioFilterType.DealScore:
          data.maxDealScore = filters.maxDealScore;
          data.minDealScore = filters.minDealScore;
          break;

        case PortfolioFilterType.LastUpdate:
          data.maxDate = filters.maxDate;
          data.minDate = filters.minDate;
          break;

        case PortfolioFilterType.PaymentsRemaining:
        case PortfolioFilterType.CurrentVehicleRate:
        case PortfolioFilterType.ReplacementVehicleRate:
        case PortfolioFilterType.CurrentVehicleMileage:
        case PortfolioFilterType.ReplacementVehicleMileage:
        case PortfolioFilterType.MonthsOfOwnership:
        case PortfolioFilterType.AgeDays:
        case PortfolioFilterType.LastServiceDate:
          masterKeyData = <MinMaxFilter<number>>filters[masterFilterKey.valueOf()];
          data[masterFilterKey] = masterKeyData;
          break;

        case PortfolioFilterType.IsTrimConfirmationRequired:
          // As this is special case, we need to refresh data as user click on toggle.
          const trims = currentView.selectedFilters.currentVehicleTrims;
          const filteredSelectedOptions = trims.filter(
            (x) => !!data.currentVehicleTrims.find((y) => y.key === x.key),
          );
          if (filteredSelectedOptions?.length !== trims?.length) {
            setSelectedFilter(
              PortfolioFilterType.CurrentVehicleTrims,
              filteredSelectedOptions,
              dealType,
              false,
            );
          }
          break;
        case PortfolioFilterType.ShopperActivity:
          masterKeyData = [...filters.shopperActivity];
          updateSingleSelectDropdown(masterKeyData, data, PortfolioFilterType.ShopperActivity);
          break;

        default:
          masterKeyData = [...(<FilterItemResource[]>filters[masterFilterKey.valueOf()])];
          masterKeyData.forEach((filter) => {
            const currentData = data[masterFilterKey].find((x) => x.key === filter.key);
            if (currentData) {
              filter.count = currentData.count;
            }
          });
          const newOptions = (<FilterItemResource[]>data[masterFilterKey]).filter(
            (x) => masterKeyData.findIndex((y) => y.key === x.key) === -1,
          );
          data[masterFilterKey] = [...masterKeyData, ...newOptions];
          break;
      }
    }
    updateAllDealsCount(data);
    reorderStatusFilter(data);
    resetHeaderRangeFilters(dealType);
    dispatch(setPortfolioFilters(data, dealType));
  };

  const updateSingleSelectDropdown = (
    masterKeyData: any,
    data: PortfolioFilterResource,
    filterType: PortfolioFilterType,
  ) => {
    masterKeyData.forEach((filter) => {
      const currentData = data[filterType].find((x) => x.key === filter.key);
      if (currentData) {
        filter.count = currentData.count;
      }
    });
    data[filterType] = masterKeyData;
    if (filterType === PortfolioFilterType.ShopperActivity) {
      return;
    }
    let count = 0;
    data[filterType].forEach((item) => {
      if (item.key.toLowerCase() !== 'all') {
        count += item.count;
      }
    });
    const allOption = data[filterType].find((x) => x.key.toLowerCase() === 'all');
    allOption.count = count;
  };

  const updateDefaultBpsValue = (minBps: number, maxBps: number, dealType: string): void => {
    const bpsValue = getNumericRange(minBps, maxBps);
    dispatch(setDefaultBpsValue(bpsValue, dealType));
  };

  const updateDefaultInMarketScoreValue = (
    minInMarketScore: number,
    maxInMarketScore: number,
    dealType: string,
  ): void => {
    const inMarketScoreValue = getNumericRange(minInMarketScore, maxInMarketScore);
    dispatch(setDefaultInMarketScoreValue(inMarketScoreValue, dealType));
  };

  const updateDefaultVehicleScoreValue = (
    minVehicleScore: number,
    maxVehicleScore: number,
    dealType: string,
  ): void => {
    const vehicleScoreValue = getNumericRange(minVehicleScore, maxVehicleScore);
    dispatch(setDefaultVehicleScoreValue(vehicleScoreValue, dealType));
  };

  const updateDefaultDealScoreValue = (
    minDealScore: number,
    maxDealScore: number,
    dealType: string,
  ): void => {
    const dealScoreValue = getNumericRange(minDealScore, maxDealScore);
    dispatch(setDefaultDealScoreValue(dealScoreValue, dealType));
  };

  const updateDefaultLastUpdated = (minDate: Date, maxDate: null, dealType: string): void => {
    const lastUpdated = getLastUpdate(minDate, maxDate);
    dispatch(setDefaultLastUpdated(lastUpdated, dealType));
  };

  const getLastUpdate = (minDate: Date, maxDate: Date): MinMaxFilter<number> => {
    if (!minDate && !maxDate) {
      return { min: null, max: null };
    }
    if (minDate != null) {
      const { days } = dateDifference(new Date(minDate.toString()));
      return { min: 0, max: days };
    }
    return { min: 0, max: 100 };
  };

  const getNumericRange = (minNumber: number, maxNumber: number): MinMaxFilter<number> => {
    if (!minNumber && !maxNumber) {
      return { min: null, max: null };
    }
    if (minNumber != null && !maxNumber) {
      return { min: minNumber, max: null };
    }
    if (maxNumber != null && !minNumber) {
      return { min: null, max: maxNumber };
    }
    return { min: minNumber, max: maxNumber };
  };

  const resetFilters = (dealType: string) => {
    dispatch(updateMasterFilterKey(null, dealType));
    dispatch(setIsPortfolioFiltersChanged(true, dealType));
    dispatch(
      updateSelectedFilters(
        {
          assignees: [],
          bpsValue: initialBpsValue,
          inMarketScore: initialInMarketScoreValue,
          vehicleScore: initialVehicleScoreValue,
          dealScore: initialDealScoreValue,
          dealTypes: [],
          lastUpdated: initialLastUpdated,
          soldBy: [],
          colors: [],
          optOutPreference: [],
          currentVehicleModels: [],
          currentVehicleTrims: [],
          currentVehicleYears: [],
          currentVehicleMakes: [],
          currentVehicleRate: null,
          incentives: [],
          replacementVehicleModels: [],
          replacementVehicleTrims: [],
          replacementVehicleYears: [],
          replacementVehicleMakes: [],
          replacementVehicleTypes: [],
          replacementVehicleRate: null,
          replacementVehicleMileage: null,
          currentVehicleTypes: null,
          replacementVehicleSelection: null,
          paymentsRemaining: null,
          isTrimConfirmationRequired: null,
          currentVehicleMileage: null,
          monthsOfOwnership: null,
          insights: [],
          ageDays: null,
          status: defaultStatus,
          lastServiceDate: null,
          recall: [],
          campaignActivityViewCounts: [],
          campaignExpiryDates: [],
          fordIncentiveNames: [],
          shopperActivity: { key: 'None', label: 'None' },
        },
        dealType,
      ),
    );
  };

  const cleanStatusFilter = (newValue: FilterStatusItemType, dealType: string) => {
    const currentView = getPortfolioView(dealType);
    const selectedFilters = currentView.selectedFilters;
    const filters = currentView.filters;

    if (newValue.key === selectedFilters.status.key) {
      if (newValue.subStatuses && newValue.subStatuses.length) {
        const filterStatus = filters.status.find((x) => x.key === newValue.key);
        newValue.subStatuses = newValue.subStatuses.filter(
          (x) => !!filterStatus.subStatuses.find((y) => y.key === x.key),
        );
      }
    }
    return newValue;
  };

  const updateAllDealsCount = (data: PortfolioFilterResource) => {
    const allDeals = data.status.find((x) => x.key === null);
    const totalCount = data.status
      .filter((x) => x.key !== null)
      .reduce(function (a, b) {
        return a + b.count;
      }, 0);
    allDeals.count = totalCount;
  };

  const reorderStatusFilter = (data: PortfolioFilterResource) => {
    const statuses = data.status;
    const newStatuses: FilterStatusItemType[] = [];
    const allDeals = data.status.find((x) => x.key === null);
    newStatuses.push(allDeals);
    pitchStatus.forEach((x) => {
      const status = statuses.find((y) => y.label === x.name);
      if (status) {
        const newStatus = { ...status };
        if (status.subStatuses && status.subStatuses.length > 0) {
          newStatus.subStatuses = [];
          const all = status.subStatuses.find((x) => x.key === null);
          newStatus.subStatuses.push(all);
          x.subStatuses.forEach((y) => {
            const subStatus = status.subStatuses.find((z) => z.label === y.name);
            if (subStatus) {
              newStatus.subStatuses.push({ ...subStatus });
            }
          });
        }
        newStatuses.push(newStatus);
      }
    });
    data.status = newStatuses;
  };

  const resetHeaderRangeFilters = (dealType: string) => {
    const currentView = getPortfolioView(dealType);
    const selectedFilters = currentView.selectedFilters;
    const newSelectedFilters = { ...selectedFilters };
    dispatch(updateSelectedFilters(newSelectedFilters, dealType));
  };

  const cleanMultiFilters = (
    options: FilterItemResource[],
    selectedOptions: FilterItemResource[],
  ) => {
    selectedOptions = selectedOptions.filter((x) => !!options.find((y) => y.key === x.key));
    return selectedOptions;
  };

  const cleanMultiBrandFilters = (
    options: FilterItemBrandResource[],
    selectedOptions: FilterItemBrandResource[],
  ) => {
    selectedOptions = selectedOptions.filter((x) => !!options.find((y) => y.brandId === x.brandId));
    return selectedOptions;
  };

  const getSelectionToggleType = (
    filter: PortfolioFilterType,
    dealType: string,
    values: FilterItemResource[],
  ) => {
    const currentView = getPortfolioView(dealType);
    if (
      !currentView.masterFilterKey ||
      values?.length === 0 ||
      currentView.masterFilterKey === filter
    ) {
      return SelectionToggleType.SelectUnselect;
    } else {
      return SelectionToggleType.Reset;
    }
  };

  const createInsightsFilter = (dealType: string, filteredDealTypes?: number[]) => {
    let insights: FilterItemResource[] = [];
    insights.push({ key: 'inMarket', label: 'Household In-Market' });
    insights.push({ key: 'isConquest', label: 'Service-to-Sales' });

    if (
      (dealType === 'all' && filteredDealTypes?.includes(2)) ||
      (dealType === 'all' && filteredDealTypes?.length === 0) ||
      dealType === 'finance'
    ) {
      insights.push({ key: 'isBalloon', label: 'Balloon Payment' });
    }
    if (featureToggle.privateIncentive) {
      insights.push({ key: 'isPrivateIncentiveApplied', label: 'Private Incentive' });
    }

    insights.push({ key: 'isLeaseBuyout', label: 'Lease Buyout' });
    insights.push({ key: 'wasMailerSent', label: 'Campaign Received' });
    insights.push({ key: 'isEv', label: 'EV' });

    if (featureToggle.recall) {
      insights.push({ key: 'hasOpenRecall', label: 'Recall' });
    }

    if (storeServices.hasTransUnion) {
      insights.push({ key: 'transUnionUnconfirmed', label: 'TransUnion Unconfirmed' });
    }

    if (storeServices.hasCpo) {
      insights.push({ key: 'isCpo', label: 'Certified Pre-owned' });
    }

    if (featureToggle?.fordPrivateIncentive && fMCBrands) {
      insights.push({ key: 'isFmcPrivateOffer', label: 'FMC Private Offer' });
    }

    insights = _.sortBy(insights, 'label');

    return insights;
  };

  const getRequestResourceByDealType = (
    postData: PortfolioRequestResource,
    dealType: string,
    previousDealType: string,
    setSearchText: (searchText: string) => void,
  ): PortfolioRequestResource => {
    let currentDealTypeRequest: PortfolioRequestResource;
    if (previousDealType !== dealType) {
      switch (dealType) {
        case 'all':
          if (allView.portfolioRequest !== null) {
            currentDealTypeRequest = { ...postData, ...allView.portfolioRequest };
            setSearchText(currentDealTypeRequest.search);
          } else {
            currentDealTypeRequest = postData;
          }
          break;

        case DealType.Finance.toLowerCase():
          if (financeView.portfolioRequest !== null) {
            currentDealTypeRequest = { ...postData, ...financeView.portfolioRequest };
            setSearchText(currentDealTypeRequest.search);
          } else {
            currentDealTypeRequest = postData;
          }
          break;

        case DealType.Lease.toLowerCase():
          if (leaseView.portfolioRequest !== null) {
            currentDealTypeRequest = { ...postData, ...leaseView.portfolioRequest };
            setSearchText(currentDealTypeRequest.search);
          } else {
            currentDealTypeRequest = postData;
          }
          break;

        case DealType.Cash.toLowerCase():
          if (cashView.portfolioRequest !== null) {
            currentDealTypeRequest = { ...postData, ...cashView.portfolioRequest };
            setSearchText(currentDealTypeRequest.search);
          } else {
            currentDealTypeRequest = postData;
          }
          break;
      }
    } else {
      currentDealTypeRequest = postData;
    }
    return currentDealTypeRequest;
  };

  const getPortfolioRequestResource = (
    sort: SortType,
    searchText: string,
    pageSize: number,
    currentPage: number,
    dealType: string,
    previousDealType: string,
    setSearchText: (searchText: string) => void,
  ): PortfolioRequestResource => {
    const currentView = getPortfolioView(dealType);
    const selectedFilters = currentView.selectedFilters;
    const postData: PortfolioRequestResource = {
      currentPage: currentPage,
      recordsPerPage: pageSize,
      search: searchText,
      sort: sort.accessor,
      sortDirection: sort.sortDirection,
      dealType: getDealTypeNumber(dealType),
      status: selectedFilters.status?.key,
      subStatus: selectedFilters.status?.subStatuses?.map((x) => x.key),
      bps: {
        min: selectedFilters.bpsValue && selectedFilters.bpsValue?.min,
        max:
          selectedFilters.bpsValue && selectedFilters.bpsValue.max
            ? selectedFilters.bpsValue.max
            : null,
      },
      inMarketScore: {
        min: selectedFilters.inMarketScore && selectedFilters.inMarketScore?.min,
        max:
          selectedFilters.inMarketScore && selectedFilters.inMarketScore.max
            ? selectedFilters.inMarketScore.max
            : null,
      },
      vehicleScore: {
        min: selectedFilters.vehicleScore && selectedFilters.vehicleScore?.min,
        max:
          selectedFilters.vehicleScore && selectedFilters.vehicleScore.max
            ? selectedFilters.vehicleScore.max
            : null,
      },
      dealScore: {
        min: selectedFilters.dealScore && selectedFilters.dealScore?.min,
        max:
          selectedFilters.dealScore && selectedFilters.dealScore.max
            ? selectedFilters.dealScore.max
            : null,
      },
      colors:
        selectedFilters.colors && selectedFilters.colors.length
          ? selectedFilters.colors.map((x) => x.key)
          : null,
      optOutPreference: selectedFilters.optOutPreference?.length
        ? selectedFilters.optOutPreference.map((x) => x.key)
        : null,
      currentModels:
        selectedFilters.currentVehicleModels && selectedFilters.currentVehicleModels.length
          ? selectedFilters.currentVehicleModels.map((x) => x.key)
          : null,
      currentTrims:
        selectedFilters.currentVehicleTrims && selectedFilters.currentVehicleTrims.length
          ? selectedFilters.currentVehicleTrims.map((x) => x.key)
          : null,
      currentYears:
        selectedFilters.currentVehicleYears && selectedFilters.currentVehicleYears.length
          ? selectedFilters.currentVehicleYears.map((x) => x.key)
          : null,
      currentVehicleMakes:
        selectedFilters.currentVehicleMakes && selectedFilters.currentVehicleMakes.length
          ? Array.from(
            new Set(
              [].concat.apply(
                [],
                selectedFilters.currentVehicleMakes.map((x) => x.dealerIds),
              ),
            ),
          )
          : null,
      currentVehicleBrandIds:
        selectedFilters.currentVehicleMakes && selectedFilters.currentVehicleMakes.length
          ? selectedFilters.currentVehicleMakes.map((x) => x.brandId)
          : null,
      currentVehicleType:
        !selectedFilters.currentVehicleTypes || selectedFilters.currentVehicleTypes.key === 'All'
          ? null
          : selectedFilters.currentVehicleTypes.key === 'New'
            ? 1
            : 0,
      replacementVehicleSelection:
        !selectedFilters.replacementVehicleSelection ||
          selectedFilters.replacementVehicleSelection.key === 'Primary'
          ? 1
          : selectedFilters.replacementVehicleSelection.key === 'Secondary'
            ? 2
            : null,
      assigneeNames:
        selectedFilters.assignees && selectedFilters.assignees.length
          ? selectedFilters.assignees.map((x) => x.key)
          : null,
      replacementModels:
        selectedFilters.replacementVehicleModels && selectedFilters.replacementVehicleModels.length
          ? selectedFilters.replacementVehicleModels.map((x) => x.key)
          : null,
      replacementTrims:
        selectedFilters.replacementVehicleTrims && selectedFilters.replacementVehicleTrims.length
          ? selectedFilters.replacementVehicleTrims.map((x) => x.key)
          : null,
      replacementVehicleMakes:
        selectedFilters.replacementVehicleMakes && selectedFilters.replacementVehicleMakes.length
          ? Array.from(
            new Set(
              [].concat.apply(
                [],
                selectedFilters.replacementVehicleMakes.map((x) => x.dealerIds),
              ),
            ),
          )
          : null,
      replacementVehicleBrandIds:
        selectedFilters.replacementVehicleMakes && selectedFilters.replacementVehicleMakes.length
          ? selectedFilters.replacementVehicleMakes.map((x) => x.brandId)
          : null,
      replacementYears:
        selectedFilters.replacementVehicleYears && selectedFilters.replacementVehicleYears.length
          ? selectedFilters.replacementVehicleYears.map((x) => x.key)
          : null,
      filteredDealTypes:
        selectedFilters.dealTypes && selectedFilters.dealTypes.length
          ? selectedFilters.dealTypes.map((x) => parseInt(x.key))
          : [],
      replacementVehicleTypes:
        selectedFilters.replacementVehicleTypes && selectedFilters.replacementVehicleTypes.length
          ? selectedFilters.replacementVehicleTypes.map((x) => x.key)
          : [],
      lastUpdated: {
        min: selectedFilters.lastUpdated && selectedFilters.lastUpdated?.min,
        max:
          selectedFilters.lastUpdated && selectedFilters.lastUpdated.max
            ? selectedFilters.lastUpdated.max + 1
            : null,
      },
      paymentsRemaining: {
        min: selectedFilters.paymentsRemaining && selectedFilters.paymentsRemaining?.min,
        max: selectedFilters.paymentsRemaining && selectedFilters.paymentsRemaining?.max,
      },
      currentVehicleRate: {
        min: selectedFilters.currentVehicleRate && selectedFilters.currentVehicleRate?.min,
        max: selectedFilters.currentVehicleRate && selectedFilters.currentVehicleRate?.max,
      },
      replacementVehicleRate: {
        min: selectedFilters.replacementVehicleRate && selectedFilters.replacementVehicleRate?.min,
        max: selectedFilters.replacementVehicleRate && selectedFilters.replacementVehicleRate?.max,
      },
      currentVehicleMileage: {
        min: selectedFilters.currentVehicleMileage && selectedFilters.currentVehicleMileage?.min,
        max:
          selectedFilters.currentVehicleMileage && selectedFilters.currentVehicleMileage.max
            ? selectedFilters.currentVehicleMileage.max
            : null,
      },
      replacementVehicleMileage: {
        min:
          selectedFilters.replacementVehicleMileage &&
          selectedFilters.replacementVehicleMileage?.min,
        max:
          selectedFilters.replacementVehicleMileage && selectedFilters.replacementVehicleMileage.max
            ? selectedFilters.replacementVehicleMileage.max
            : null,
      },
      monthsOfOwnership: {
        min: selectedFilters.monthsOfOwnership && selectedFilters.monthsOfOwnership?.min,
        max:
          selectedFilters.monthsOfOwnership && selectedFilters.monthsOfOwnership.max
            ? selectedFilters.monthsOfOwnership.max
            : null,
      },
      ageDays: {
        min: selectedFilters.ageDays && selectedFilters.ageDays?.min,
        max:
          selectedFilters.ageDays && selectedFilters.ageDays.max
            ? selectedFilters.ageDays.max + 1
            : null,
      },
      lastServiceDate: {
        min: selectedFilters.lastServiceDate && selectedFilters.lastServiceDate?.min,
        max:
          selectedFilters.lastServiceDate && selectedFilters.lastServiceDate.max
            ? selectedFilters.lastServiceDate.max + 1
            : null,
      },
      isTrimConfirmationRequired: selectedFilters.isTrimConfirmationRequired,
      soldBy:
        selectedFilters.soldBy && selectedFilters.soldBy.length
          ? selectedFilters.soldBy.map((x) => x.key)
          : [],
      incentives:
        selectedFilters.incentives && selectedFilters.incentives.length
          ? selectedFilters.incentives.map((x) => x.key)
          : [],
      insights:
        selectedFilters.insights && selectedFilters.insights.length
          ? selectedFilters.insights.map((x) => x.key)
          : [],
      recall:
        selectedFilters.recall && selectedFilters.recall.length
          ? selectedFilters.recall.map((x) => x.key)
          : [],
      campaignActivityViewCounts:
        selectedFilters.campaignActivityViewCounts &&
          selectedFilters.campaignActivityViewCounts.length
          ? selectedFilters.campaignActivityViewCounts.map((x) => x.key)
          : [],
      campaignExpiryDates:
        selectedFilters.campaignExpiryDates && selectedFilters.campaignExpiryDates.length
          ? selectedFilters.campaignExpiryDates.map((x) => x.key)
          : [],
      fordIncentiveNames:
        selectedFilters.fordIncentiveNames && selectedFilters.fordIncentiveNames.length
          ? selectedFilters.fordIncentiveNames.map((x) => x.key)
          : [],
      shopperActivity:
        !selectedFilters.shopperActivity || selectedFilters.shopperActivity.key === 'None'
          ? null
          : [selectedFilters.shopperActivity.key],
    };
    return getRequestResourceByDealType(postData, dealType, previousDealType, setSearchText);
  };

  return {
    getPortfolioView,
    setSelectedFilter,
    updateDefaultBpsValue,
    updateDefaultInMarketScoreValue,
    updateDefaultVehicleScoreValue,
    updateDefaultDealScoreValue,
    updateDefaultLastUpdated,
    resetFilters,
    hasFilters,
    updateWithMasterFilter,
    getLastUpdate,
    getPortfolioRequestResource,
    getSelectionToggleType,
    createInsightsFilter,
  };
};

export type PortfolioFilterBoxType = {
  getPortfolioView: (dealType: string) => PortfolioView;
  setSelectedFilter: (
    key: PortfolioFilterType,
    data:
      | FilterStatusItemType
      | FilterItemResource
      | FilterItemResource[]
      | MinMaxFilter<number>
      | MinMaxFilter<Date>
      | boolean,
    dealType: string,
    triggerSearch?: boolean,
  ) => void;
  updateDefaultBpsValue: (minBps: number, maxBps: number, dealType: string) => void;
  updateDefaultInMarketScoreValue: (
    minInMarketScore: number,
    maxInMarketScore: number,
    dealType: string,
  ) => void;
  updateDefaultVehicleScoreValue: (
    minVehicleScore: number,
    maxVehicleScore: number,
    dealType: string,
  ) => void;
  updateDefaultDealScoreValue: (
    minDealScore: number,
    maxDealScore: number,
    dealType: string,
  ) => void;
  updateDefaultLastUpdated: (minDate: Date, maxDate: Date, dealType: string) => void;
  resetFilters: (dealType: string) => void;
  hasFilters: (dealType: string) => boolean;
  updateWithMasterFilter: (data: PortfolioFilterResource, dealType: string) => void;
  getLastUpdate: (minDate: Date, maxDate: Date) => MinMaxFilter<number>;
  getPortfolioRequestResource: (
    sort: SortType,
    searchText: string,
    pageSize: number,
    currentPage: number,
    dealType: string,
    previousDealType: string,
    setSearchText: (searchText: string) => void,
  ) => PortfolioRequestResource;
  getSelectionToggleType: (
    filter: PortfolioFilterType,
    dealType: string,
    values: FilterItemResource[],
  ) => SelectionToggleType;
  createInsightsFilter: (dealType: string, filteredDealTypes?: number[]) => FilterItemResource[];
};
